/**
 * Recently-viewed product tracking + the cart drawer's "Recently viewed"
 * tab panel. Loaded on every page (from snippets/cart-drawer.liquid, which
 * ships site-wide via layout/theme.liquid) so a product page visit is
 * recorded no matter where the visitor lands.
 *
 * - Recording reads the current URL rather than a server-rendered handle,
 *   so this file works without any change to the product page template.
 * - <recently-viewed-panel> (sections/recently-viewed-card.liquid) is
 *   inert until its load() method is called (by the tab controller in
 *   cart.js, on first activation of the "Recently viewed" tab) - per spec
 *   this must not fetch anything before the tab is actually opened, and
 *   #CartDrawerContent gets replaced wholesale on every cart refresh so
 *   there's nothing to cache across that swap anyway.
 * - Each rendered row ends in a pill button: single-variant products get
 *   "Add" (added straight to cart via window.CartAPI.add, no navigation);
 *   multi-variant products get "View", which opens assets/quick-view.js's
 *   modal for that product.
 */
(function () {
  var STORAGE_KEY = 'waimers:recently-viewed';
  var MAX_STORED = 12;
  var MAX_RENDERED = 6;
  var SECTION_ID = 'recently-viewed-card';

  function readHandles() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var handles = raw ? JSON.parse(raw) : [];
      return Array.isArray(handles) ? handles.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      // localStorage unavailable (private browsing, quota) - recording is
      // best-effort and silently no-ops.
    }
  }

  function recordCurrentProduct() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    var handle = match && match[1];
    if (!handle) return;

    var handles = readHandles().filter((h) => h !== handle);
    handles.unshift(handle);
    if (handles.length > MAX_STORED) handles.length = MAX_STORED;
    writeHandles(handles);
  }

  recordCurrentProduct();

  if (!customElements.get('recently-viewed-panel')) {
    class RecentlyViewedPanel extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      load() {
        if (this.dataset.loaded) return;
        this.dataset.loaded = 'true';

        var handles = readHandles().slice(0, MAX_RENDERED);
        if (!handles.length) return; // status element already shows the empty message

        var statusEl = this.querySelector('[data-recently-viewed-status]');
        var listEl = this.querySelector('[data-recently-viewed-list]');
        var base = this.dataset.productsBase || '/products/';

        Promise.all(
          handles.map((handle) =>
            fetch(base + encodeURIComponent(handle) + '?section_id=' + SECTION_ID, { headers: { Accept: 'text/html' } })
              .then((response) => (response.ok ? response.text() : null))
              .catch(() => null)
          )
        ).then((results) => {
          var rowsHtml = results
            .filter(Boolean)
            .map((html) => {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var card = doc.querySelector('[data-recently-viewed-card]');
              return card ? card.innerHTML : '';
            })
            .filter(Boolean)
            .join('');

          if (!rowsHtml) return;
          if (listEl) listEl.innerHTML = rowsHtml;
          if (statusEl) statusEl.hidden = true;

          /* Same reason as the wishlist grid: fetched markup never sees
             DOMContentLoaded or shopify:section:load, so its media would
             shimmer as "loading" indefinitely. */
          if (listEl && window.ThemeReveal) window.ThemeReveal.init(listEl);
        });
      }

      onClick(event) {
        var addButton = event.target.closest('[data-recent-add]');
        if (addButton) {
          this.addToCart(addButton);
          return;
        }

        var viewButton = event.target.closest('[data-recent-view]');
        if (viewButton) {
          var modal = document.getElementById('QuickViewModal');
          if (modal && typeof modal.open === 'function') {
            modal.open(viewButton.dataset.productUrl, viewButton);
          }
        }
      }

      addToCart(button) {
        if (button.disabled || button.classList.contains('is-loading')) return;
        if (!window.CartAPI || typeof window.CartAPI.add !== 'function') return;

        var variantId = button.dataset.variantId;
        if (!variantId) return;

        button.classList.add('is-loading');
        button.disabled = true;

        var formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        window.CartAPI.add(formData)
          .catch(function (error) {
            console.error('[cart] recently-viewed add failed', error);
          })
          .finally(function () {
            button.classList.remove('is-loading');
            button.disabled = false;
          });
      }
    }

    customElements.define('recently-viewed-panel', RecentlyViewedPanel);
  }
})();
